import React, { useState } from 'react';
import {
  Card,
  Upload,
  Button,
  Table,
  Select,
  Input,
  Form,
  message,
  Row,
  Col,
  Divider,
  Radio,
  Icon,
  Typography,
  Alert,
  Space
} from 'antd';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
// Code generators are shared with the Smart Asset Import module (single source of truth).
import { generateLocationCode, generateAssetSubtypeCode } from './utils/assetCodeGen';

const { Option } = Select;
const { Title, Text } = Typography;
const { Dragger } = Upload;

// Required fields from the image
const REQUIRED_FIELDS = [
  { key: 'Sn No', label: 'Sn No (Serial Number)', required: true, autoGenerated: false },
  { key: 'Asset Type', label: 'Asset Type', required: true, autoGenerated: false },
  { key: 'Asset Sub Type', label: 'Asset Sub Type', required: true, autoGenerated: false },
  { key: 'Model', label: 'Model', required: true, autoGenerated: false },
  { key: 'Make', label: 'Make', required: true, autoGenerated: false },
  { key: 'Description', label: 'Description', required: false, autoGenerated: false },
  { key: 'Location', label: 'Location', required: true, autoGenerated: false },
  { key: 'Location Code', label: 'Location Code', required: true, autoGenerated: true },
  { key: 'Sub Location', label: 'Sub Location', required: true, autoGenerated: false },
  { key: 'Sub Location Code', label: 'Sub Location Code', required: true, autoGenerated: true },
  { key: 'Owner', label: 'Owner', required: false, autoGenerated: false },
  { key: 'Purchase Date', label: 'Purchase Date', required: false, autoGenerated: false },
  { key: 'Purchase Vendor', label: 'Purchase Vendor', required: false, autoGenerated: false },
  { key: 'Purchase Invoice No', label: 'Purchase Invoice No', required: false, autoGenerated: false },
  { key: 'Purchase Amount', label: 'Purchase Amount', required: false, autoGenerated: false },
  { key: 'Asset Subtype Code', label: 'Asset Subtype Code', required: true, autoGenerated: true },
  { key: 'Owner Code', label: 'Owner Code', required: false, autoGenerated: false },
  { key: 'QTY', label: 'QTY', required: true, autoGenerated: false }
];

function AssetImportMapper() {
  const [fileData, setFileData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [fieldMappings, setFieldMappings] = useState({});
  const [unmappedFields, setUnmappedFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: upload, 2: mapping, 3: preview

  // Handle file upload
  const handleFileUpload = (file) => {
    setLoading(true);
    const reader = new FileReader();
    const fileName = file.name.toLowerCase();
    
    reader.onload = (e) => {
      try {
        let data = null;
        
        if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          // Excel file
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const firstSheet = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheet];
          data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (data.length === 0) {
            throw new Error('Excel file is empty');
          }
          
          // Convert to object array
          const headerRow = data[0];
          const rows = data.slice(1).map(row => {
            const obj = {};
            headerRow.forEach((header, idx) => {
              obj[header] = row[idx] !== undefined ? row[idx] : '';
            });
            return obj;
          });
          data = rows;
          
          if (data && data.length > 0) {
            setFileData(data);
            setHeaders(headerRow.filter(h => h));
            setStep(2);
            message.success(`Loaded ${data.length} rows from Excel file`);
          } else {
            throw new Error('No data found in file');
          }
        } else if (fileName.endsWith('.csv')) {
          // CSV file
          Papa.parse(e.target.result, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              if (results.errors.length > 0) {
                console.warn('CSV parsing warnings:', results.errors);
              }
              if (results.data && results.data.length > 0) {
                setFileData(results.data);
                setHeaders(Object.keys(results.data[0] || {}));
                setStep(2);
                message.success(`Loaded ${results.data.length} rows from CSV`);
              } else {
                message.error('No data found in CSV file');
                setFileData(null);
                setHeaders([]);
              }
              setLoading(false);
            },
            error: (error) => {
              message.error(`Error parsing CSV: ${error.message}`);
              setFileData(null);
              setHeaders([]);
              setLoading(false);
            }
          });
          return; // CSV parsing is async
        } else {
          throw new Error('Unsupported file type. Please upload CSV or Excel file.');
        }
      } catch (error) {
        message.error(`Error loading file: ${error.message}`);
        setFileData(null);
        setHeaders([]);
        setLoading(false);
      }
    };
    
    if (fileName.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
    
    return false; // Prevent auto upload
  };

  // Handle field mapping change
  const handleMappingChange = (fieldKey, headerName) => {
    setFieldMappings(prev => ({
      ...prev,
      [fieldKey]: headerName
    }));
  };

  // Handle unmapped field configuration
  const handleUnmappedFieldChange = (fieldKey, config) => {
    setUnmappedFields(prev => ({
      ...prev,
      [fieldKey]: config
    }));
  };

  // Generate final CSV
  const generateFinalCSV = () => {
    if (!fileData || fileData.length === 0) {
      message.error('No data to process');
      return;
    }

    // Validate required mappings
    const requiredFields = REQUIRED_FIELDS.filter(f => f.required && !f.autoGenerated);
    const missingMappings = requiredFields.filter(f => !fieldMappings[f.key] && !unmappedFields[f.key]);
    
    if (missingMappings.length > 0) {
      message.error(`Please map or configure the following required fields: ${missingMappings.map(f => f.label).join(', ')}`);
      return;
    }

    // Process each row
    const processedRows = fileData.map((row, index) => {
      const processedRow = {};
      
      REQUIRED_FIELDS.forEach(field => {
        if (field.autoGenerated) {
          // Auto-generate codes
          if (field.key === 'Location Code') {
            const locationValue = fieldMappings['Location'] 
              ? (row[fieldMappings['Location']] || '')
              : (unmappedFields['Location']?.value || '');
            processedRow[field.key] = generateLocationCode(locationValue);
          } else if (field.key === 'Sub Location Code') {
            const subLocationValue = fieldMappings['Sub Location']
              ? (row[fieldMappings['Sub Location']] || '')
              : (unmappedFields['Sub Location']?.value || '');
            processedRow[field.key] = generateLocationCode(subLocationValue);
          } else if (field.key === 'Asset Subtype Code') {
            const subtypeValue = fieldMappings['Asset Sub Type']
              ? (row[fieldMappings['Asset Sub Type']] || '')
              : (unmappedFields['Asset Sub Type']?.value || '');
            processedRow[field.key] = generateAssetSubtypeCode(subtypeValue);
          }
        } else {
          // Get value from mapping or unmapped field config
          let value = '';
          if (fieldMappings[field.key]) {
            value = row[fieldMappings[field.key]] || '';
          } else if (unmappedFields[field.key]) {
            const config = unmappedFields[field.key];
            if (config.type === 'same') {
              value = config.value || '';
            } else if (config.type === 'incremental') {
              value = config.startValue ? (parseInt(config.startValue) + index) : (index + 1);
            }
          }
          // Replace commas with spaces to prevent CSV structure issues
          processedRow[field.key] = String(value).replace(/,/g, ' ');
        }
      });
      
      return processedRow;
    });

    // Convert to CSV
    const csvHeaders = REQUIRED_FIELDS.map(f => f.key);
    const csvRows = processedRows.map(row => 
      csvHeaders.map(header => {
        const value = row[header];
        // Values already have commas replaced with spaces, but still need to handle quotes and newlines
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // Escape quotes and newlines if present
        if (stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      })
    );

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `asset-import-${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success(`Generated CSV with ${processedRows.length} rows`);
    setStep(3);
  };

  // Reset to start over
  const handleReset = () => {
    setFileData(null);
    setHeaders([]);
    setFieldMappings({});
    setUnmappedFields({});
    setStep(1);
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Asset Import Mapper</Title>
      <Text type="secondary">
        Upload an Excel or CSV file to map fields and generate an AssetPro import file
      </Text>
      
      <Divider />

      {step === 1 && (
        <Card>
          <Dragger
            accept=".xlsx,.xls,.csv"
            beforeUpload={handleFileUpload}
            showUploadList={false}
            disabled={loading}
          >
            <p className="ant-upload-drag-icon">
              <Icon type="inbox" />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">
              Support for Excel (.xlsx, .xls) and CSV (.csv) files
            </p>
          </Dragger>
          {loading && <div style={{ textAlign: 'center', marginTop: 16 }}>Loading file...</div>}
        </Card>
      )}

      {step === 2 && (
        <div>
          <Alert
            message="Map your file headers to AssetPro fields"
            description="For fields not in your file, you can either use the same value for all rows or generate incremental values."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Card title="Field Mapping" style={{ marginBottom: 24 }}>
            <Table
              dataSource={REQUIRED_FIELDS}
              rowKey="key"
              pagination={false}
              columns={[
                {
                  title: 'AssetPro Field',
                  dataIndex: 'label',
                  key: 'label',
                  width: 200,
                  render: (text, record) => (
                    <span>
                      {text}
                      {record.required && <span style={{ color: 'red' }}> *</span>}
                      {record.autoGenerated && (
                        <span style={{ color: '#1890ff', fontSize: '12px', marginLeft: 8 }}>
                          (Auto-generated)
                        </span>
                      )}
                    </span>
                  )
                },
                {
                  title: 'Map to File Header',
                  key: 'mapping',
                  render: (_, record) => {
                    if (record.autoGenerated) {
                      return (
                        <span style={{ color: '#52c41a' }}>
                          Will be auto-generated from {record.key === 'Location Code' ? 'Location' : 
                            record.key === 'Sub Location Code' ? 'Sub Location' : 'Asset Sub Type'}
                        </span>
                      );
                    }
                    return (
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select header from file"
                        value={fieldMappings[record.key] || undefined}
                        onChange={(value) => handleMappingChange(record.key, value)}
                        allowClear
                      >
                        {headers.map(header => (
                          <Option key={header} value={header}>{header}</Option>
                        ))}
                      </Select>
                    );
                  }
                },
                {
                  title: 'Or Configure',
                  key: 'unmapped',
                  render: (_, record) => {
                    if (record.autoGenerated) return null;
                    
                    const isMapped = !!fieldMappings[record.key];
                    const unmappedConfig = unmappedFields[record.key];
                    
                    return (
                      <div>
                        {!isMapped && (
                          <Radio.Group
                            value={unmappedConfig?.type || null}
                            onChange={(e) => {
                              if (e.target.value) {
                                handleUnmappedFieldChange(record.key, {
                                  type: e.target.value,
                                  value: unmappedConfig?.value || '',
                                  startValue: unmappedConfig?.startValue || '1'
                                });
                              } else {
                                const newUnmapped = { ...unmappedFields };
                                delete newUnmapped[record.key];
                                setUnmappedFields(newUnmapped);
                              }
                            }}
                          >
                            <Radio value={null}>None</Radio>
                            <Radio value="same">Same Value</Radio>
                            <Radio value="incremental">Incremental</Radio>
                          </Radio.Group>
                        )}
                        {!isMapped && unmappedConfig?.type === 'same' && (
                          <Input
                            style={{ marginTop: 8 }}
                            placeholder="Enter value (e.g., 01-Apr-2026)"
                            value={unmappedConfig.value}
                            onChange={(e) => handleUnmappedFieldChange(record.key, {
                              ...unmappedConfig,
                              value: e.target.value
                            })}
                          />
                        )}
                        {!isMapped && unmappedConfig?.type === 'incremental' && (
                          <Input
                            style={{ marginTop: 8 }}
                            placeholder="Start value (e.g., 1)"
                            value={unmappedConfig.startValue}
                            onChange={(e) => handleUnmappedFieldChange(record.key, {
                              ...unmappedConfig,
                              startValue: e.target.value
                            })}
                          />
                        )}
                      </div>
                    );
                  }
                }
              ]}
            />
          </Card>

          <Row gutter={16}>
            <Col>
              <Button onClick={handleReset}>Reset</Button>
            </Col>
            <Col>
              <Button type="primary" onClick={generateFinalCSV}>
                Generate Import CSV
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {step === 3 && (
        <Card>
          <Alert
            message="CSV Generated Successfully!"
            description="Your AssetPro import file has been downloaded. You can now import it into AssetPro."
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Button onClick={handleReset}>Start Over</Button>
        </Card>
      )}
    </div>
  );
}

export default AssetImportMapper;
